import React from "react";
import styled from "styled-components";
import axios from "axios";
import Image from "next/image";
import * as Router from "next/router";

import * as gtag from "../utils/gtag";
import * as u from "../utils";
import * as I from "./Icons";
import * as T from "./Typography";
import Button from "./Button";

export default function PromoBanner(props) {
  const router = Router.useRouter();
  const [banner, setBanner] = React.useState();

  React.useEffect(() => getBanner(), []);

  if (!banner) return null;

  return (
    <S.BannerContainer
      isUpsell={banner.upsell}
      imageOnLeft={banner.imageOnLeft}
      data-cy="banner"
    >
      {banner.image && (
        <S.ImageWrap>
          <Image src={banner.image.url} layout="fill" objectFit="cover" />
        </S.ImageWrap>
      )}
      <S.ContentContainer hasImage={banner.image} hasCopy={banner.copy}>
        <TextContent />
        <S.ButtonWrap hasImage={banner.image} hasCopy={banner.copy}>
          <Button size="sml" isWide={banner.image} onClick={() => onClick()}>
            {banner.buttonText}
            {banner.upsell && <S.LockIcon />}
          </Button>
        </S.ButtonWrap>
      </S.ContentContainer>
    </S.BannerContainer>
  );

  function TextContent() {
    if (banner.image)
      return (
        <>
          {banner.description && <T.H6>{banner.description}</T.H6>}
          <T.H3Rox>{banner.title}</T.H3Rox>
          <T.CM>{banner.copy}</T.CM>
        </>
      );

    return (
      <S.TextWrap>
        {banner.description && <T.H6>{banner.description}</T.H6>}
        <T.H3Rox>{banner.title}</T.H3Rox>
        <T.CM>{banner.copy}</T.CM>
      </S.TextWrap>
    );
  }

  async function getBanner() {
    if (props.banner) return setBanner(props.banner);

    const url = "/api/contentful.get-banner-by-page";
    const body = { route: router.route };
    const { data: priorityBanner } = await axios.post(url, body);
    setBanner(priorityBanner);
  }

  async function onClick() {
    gtag.event({
      name: gtag.GA_EVENT.PLATFORM_ENGAGEMENT,
      category: "Engagement",
      label: "Click Promo banner",
      value: banner.title,
    });

    router.push(banner.buttonUrl);
  }
}

const S = {};

S.BannerContainer = styled.div`
  background-color: #f3e9e2;
  border-radius: 5px;
  display: flex;
  flex-direction: column;
  margin: 10px 0 20px 0;
  overflow: hidden;
  position: relative;
  width: 100%;
  ${(p) =>
    p.isUpsell &&
    `
    background-color: ${u.colours.white};
    border: solid ${u.colours.turquoise} 1px;
  `}
  @media ${u.device.mobile} {
    flex-direction: ${(p) => (p.imageOnLeft ? "row" : "row-reverse")};
  }
`;

S.ImageWrap = styled.div`
  height: 200px;
  position: relative;
  width: 100%;
  @media ${u.device.mobile} {
    height: 230px;
  }
  @media ${u.device.tablet} {
    height: 250px;
  }
  @media ${u.device.desktop} {
    height: 280px;
  }
`;

S.ContentContainer = styled.div`
  display: flex;
  gap: 10px;
  ${(p) =>
    p.hasCopy
      ? `
    flex-direction: column;
  `
      : `
    align-items: center;
    justify-content: space-between;
  `}
  ${(p) =>
    p.hasImage
      ? `
    justify-content: space-evenly;
    padding: 30px;
  `
      : `
    padding: 20px;
    @media ${u.device.mobile} {
      align-items: center;
      flex-direction: row;
      justify-content: space-between;
      width: 100%;
    }
  `}
`;

S.TextWrap = styled.div`
  display: flex;
  flex-direction: column;
  * + * {
    margin-top: 10px;
  }
`;

S.ButtonWrap = styled.div`
  width: fit-content;
  ${(p) => p.hasCopy && "margin: 10px 0 0 0;"}
  @media ${u.device.mobile} {
    ${(p) => !p.hasImage && `margin: 0;`}
  }
`;

S.LockIcon = styled(I.Lock)`
  margin: 0 0 4px 0;
`;
